import { useQueryClient } from "@tanstack/react-query"
import { useSharedTimeCapsuleBySlug } from "api/ApiHooks"
import { LoversApi } from "api/LoversApi"
import { Button, Input } from "components/ui"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

const LoadingView = () => {
  return (
    <main className="py-10 lg:pl-72">
      <div className="px-4 sm:px-6 lg:px-8">
        <p>Loading...</p>
      </div>
    </main>
  )
}

export const MemberTimeCapsuleSettingsPage = () => {
  const { creatorId, slug } = useParams()
  const { data, isLoading } = useSharedTimeCapsuleBySlug(creatorId || "", slug || "")

  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmName, setConfirmName] = useState<string>("")
  const [error, setError] = useState<string>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsDeleting(true)
    if (!data?.id) return

    LoversApi()
      .timeCapsule.leaveTimeCapsule(data.id)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["useSharedTimeCapsuleBySlug", creatorId, slug]
        })
        queryClient.invalidateQueries({ queryKey: ["useSharedTimeCapsules"] })
        navigate("/dashboard")
      })
      .catch((err) => {
        setError(err.response.data.message)
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }

  return (
    <>
      {isLoading || !data ? (
        <LoadingView />
      ) : (
        <div className="pb-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
              <div className="pb-12">
                <div className="mb-10">
                  <h2 className="font-semibold text-lg text-gray-900 dark:text-white">
                    {data.name} settings
                  </h2>
                  <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
                    Here are your membership settings for this time capsule.
                  </p>
                </div>

                <div className="border-t border-gray-900/10 dark:border-white/10 pt-12 pb-2">
                  <div className="space-y-8 p-4 rounded-lg shadow-md border-2 border-red-600 bg-red-50 dark:bg-red-950/20 sm:px-6">
                    <div>
                      <h1 className="text-3xl font-semibold text-red-500 mb-3">
                        Danger zone
                      </h1>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        Leave this time capsule
                      </h2>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        This action cannot be undone. You existing entries will stay in
                        the capsule but your access to it will be removed. To confirm and
                        leave, please type the time capsule name:{" "}
                        <span className="font-semibold">{data.name}</span>
                      </p>
                    </div>

                    <form onSubmit={handleDelete} className="space-y-6">
                      <div>
                        <Input
                          type="text"
                          value={confirmName}
                          onChange={(e) => setConfirmName(e.target.value)}
                          placeholder="Enter time capsule name"
                        />
                      </div>

                      {error && <p className="text-sm text-red-500">{error}</p>}

                      <Button
                        type="submit"
                        variant="danger"
                        isLoading={isDeleting}
                        disabled={confirmName !== data.name}
                      >
                        {isDeleting ? "Leaving..." : "Leave time capsule"}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
